import { ExpanderContent } from "@amzn/stencil-react-components/expander";
import { TriggerButton } from "@amzn/stencil-react-components/helpers";
import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import { H6 } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Video, { VideoProps } from "@/components/Commons/Video/Video";
import { VideoFileConfig } from "@/types/common";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

interface VideoGuidanceProps extends VideoProps {
  /** The video config which contains filename and filetype. */
  videoFileConfig: VideoFileConfig;

  /** The Snowplow event category that will be tracked by the structured event */
  eventCategory: Day1EventCategory;

  /** The data-test-id that will be assigned to the title of the video guidance expander. */
  titleDataTestId?: string;
}

/**
 * An expandable section that plays a video to guide NHs to complete some steps.
 */
export default function VideoGuidance({
  videoFileConfig,
  titleDataTestId,
  eventCategory,
  overlayTextI18nId,
}: VideoGuidanceProps): JSX.Element {
  const [isVideoExpanded, setVideoExpanded] = useState(false);
  const intl = useIntl();

  /**
   * Expand or collapse the Expander section
   */
  const toggleExpander = () => {
    const isExpanded = !isVideoExpanded;

    trackStructEvent({
      category: eventCategory,
      action: isExpanded
        ? Day1EventAction.ExpandGuidance
        : Day1EventAction.CollapseGuidance,
    });
    setVideoExpanded(isExpanded);
  };

  return (
    <Col gridGap="S200">
      <Hr />
      <Row
        alignItems="center"
        padding={{ left: "S300", right: "S300" }}
        gridGap="S200"
      >
        <TriggerButton
          iconAltText={intl.formatMessage({
            id: "videoGuidance-button-altText",
          })}
          className={"excluded-trigger-btn"}
          isExpanded={isVideoExpanded}
          onClick={toggleExpander}
          dataTestId="triggerButtonDataTestId"
        />
        <H6 color="color.link.primary-default" dataTestId={titleDataTestId}>
          <FormattedMessage id="videoGuidance-title" />
        </H6>
      </Row>
      <ExpanderContent
        isExpanded={isVideoExpanded}
        dataTestId="expanderContentDataTestId"
      >
        <Video
          videoFileConfig={videoFileConfig}
          overlayTextI18nId={overlayTextI18nId}
          data-testid="videoDataTestId"
        />
      </ExpanderContent>
      <Hr />
    </Col>
  );
}
